import { syncRefs } from '@vueuse/core';
import { onBeforeUnmount, ref, type Ref, watch, type WatchStopHandle } from 'vue';

import { logger } from '@exchange/libs/utils/simple-logger/src';

import type { FundingRateData } from './funding-rate-model';
import fundingRateService from './funding-rate.service';

export default function useFundingRate(instrumentCode: Ref<string>) {
  const fundingRateRef = ref<FundingRateData>({} as FundingRateData);
  const unsubscribe = ref<(() => void) | undefined>();
  const isLoading = ref(true);
  let stopFR: WatchStopHandle;

  const safeUnsubscribe = () => {
    stopFR?.();
    try {
      unsubscribe.value?.();
    } catch (e) {
      logger.log('FundingRate: unsubscribing failed', e);
    }
  };

  const handleSubscribe = async (instrument_code: string) => {
    safeUnsubscribe();
    isLoading.value = true;

    unsubscribe.value = fundingRateService.subscribe(instrument_code, {
      success: (res: { fundingRate: Ref<FundingRateData> }) => {
        stopFR = syncRefs(res.fundingRate, fundingRateRef);
        isLoading.value = false;
      },
      fail: (error) => {
        logger.error(`FundingRate: failed to subscribe to ${instrument_code}, retrying...`, error);
        handleSubscribe(instrument_code);
      },
    });
  };

  watch(
    instrumentCode,
    async (code) => {
      if (!code) return;
      await handleSubscribe(code);
    },
    { immediate: true },
  );

  onBeforeUnmount(() => {
    safeUnsubscribe();
  });

  return {
    fundingRate: fundingRateRef,
    isLoading,
  };
}
